import { ComponentTypes } from "../../../src/taskpane/config/config";
import { ContentManagerCopyMoveModeEnum } from "../../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import type { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import type { IContentManagerMoveTo, currentSharepointFolderState } from "../../../src/taskpane/redux/models/reduxModels";

/**
 * State for the persistent "a copy/move is waiting for a destination" tray.
 *
 * Why a tray at all: an armed copy/move used to be visible ONLY inside a document
 * library, where the command bar quietly collapsed to Confirm/Cancel. Every other
 * screen — the locations list, the all-documents grid, Home — showed nothing, no
 * count, and offered no way to cancel. The aggregate flow drops the user straight
 * into the locations list, so it was invisible from the moment it started.
 *
 * Two rules this encodes that are easy to get wrong:
 *
 * 1. **Gate on `moveToEnabled`, never on `mode`.** `mode` cannot be cleared through
 *    setContentManagerMoveTo (`null == undefined`, so the merge ignores it), so a
 *    mode-based gate would leave the tray up forever after a cancel. `moveToEnabled`
 *    also has the right lifecycle: it drops as the copy job starts, handing progress
 *    reporting over to the existing spinner instead of showing two at once.
 *
 * 2. **A pending permission check is NOT permission to commit.** getFolderPermissionMenuState
 *    reports `loading`/`unknown` as render-enabled-but-guarded, so a button driven off it
 *    looks clickable and then silently does nothing. Worse, currentSharepointFolder is only
 *    maintained by the library view, so in the locations list it still holds the PREVIOUS
 *    library's loaded permissions. Hence: require a real destination first, and treat a
 *    pending check as blocked rather than allowed.
 *
 * 3. **Nothing may be committed while the destination is loading.** The tray is mounted
 *    above ContentManagerBody's loading branch, on purpose — an armed operation has to
 *    stay visible while the user navigates. But the confirm dialog it drives lives in
 *    ContentBodyFilesFolders, which that same branch unmounts. Entering a library also
 *    pushes the working directory BEFORE the load finishes, so without this check the
 *    button would go live — judged against the PREVIOUS library's cached permissions —
 *    with nothing mounted to receive the request. It would then sit in redux and open
 *    the dialog unbidden in whichever library the user opened next.
 */

export type CopyMoveTrayBlocker =
    /** Not inside a document library or folder yet. */
    | "noDestination"
    /** SharePoint lists are excluded as destinations, as in both menu hooks. */
    | "unsupportedDestination"
    /** Moving into the folder the items already live in. */
    | "sameAsSource"
    /**
     * Some of the queued items are already in THIS folder. Distinct from sameAsSource,
     * which can only see the operation's single declared source: an aggregate selection
     * spans libraries, so this is decided per row and published by the library view.
     */
    | "alreadyAtDestination"
    /** The destination itself is still loading. See the note on rule 3. */
    | "destinationLoading"
    /** Destination permissions still resolving — disabled, not silently inert. */
    | "permissionPending"
    | "noPermission";

export interface ICopyMoveTrayState {
    visible: boolean;
    /** Move/MoveAll vs Copy/CopyAll — drives the verb and the same-as-source rule. */
    isMoveOperation: boolean;
    /** MoveAll/CopyAll operate on the whole library; the count is not meaningful. */
    isAllItems: boolean;
    itemCount: number;
    /** Display label for the source; a folder path, or a label when there is no single folder. */
    sourceLabel: string;
    canCommit: boolean;
    blocker: CopyMoveTrayBlocker | null;
}

export interface ICopyMoveTrayInputs {
    moveTo?: IContentManagerMoveTo;
    currentDirectory?: IPageSettings | null;
    currentSharepointFolder?: currentSharepointFolderState;
    /** Blocks the commit; see rule 3. Omitted by the visibility-only callers. */
    isContentLoading?: boolean;
}

const HIDDEN: ICopyMoveTrayState = {
    visible: false,
    isMoveOperation: false,
    isAllItems: false,
    itemCount: 0,
    sourceLabel: "",
    canCommit: false,
    blocker: null,
};

const DOCUMENT_MODES = [
    ContentManagerCopyMoveModeEnum.Move,
    ContentManagerCopyMoveModeEnum.Copy,
    ContentManagerCopyMoveModeEnum.MoveAll,
    ContentManagerCopyMoveModeEnum.CopyAll,
];

const resolveBlocker = (
    moveTo: IContentManagerMoveTo,
    isMoveOperation: boolean,
    currentDirectory?: IPageSettings | null,
    currentSharepointFolder?: currentSharepointFolderState,
    isContentLoading?: boolean
): CopyMoveTrayBlocker | null => {
    // A destination must be an actual library or folder. Checked FIRST so a stale
    // permission snapshot from a previously visited library can never make the
    // locations list look like a valid destination.
    if (!currentDirectory?.sharePointListId) {
        return "noDestination";
    }
    if (currentDirectory.type === ComponentTypes.SharePointList) {
        return "unsupportedDestination";
    }

    // Copy into the source folder is legitimate (SharePoint keeps both); a move is not.
    //
    // This must stay the EXACT complement of the commit dialog's render condition in
    // ContentBodyFilesFolders:
    //     mode === Copy || mode === CopyAll || currentDirectory?.id !== moveToDirectory?.id
    // where moveToDirectory is this same last working directory. If the tray were to
    // enable its button in any case that condition rejects, the click would open nothing
    // — reintroducing the silent no-op this tray exists to remove. So: no extra guard on
    // sourceDirectory?.id, which would let the both-undefined case through.
    const sourceDirectory = moveTo.workingDirectories?.length > 0
        ? moveTo.workingDirectories[moveTo.workingDirectories.length - 1]
        : null;
    if (isMoveOperation && currentDirectory.id === sourceDirectory?.id) {
        return "sameAsSource";
    }

    // Per-row equivalent of the above, for selections with no single source. Safe to
    // trust a stale value: it can only linger once the library view has unmounted, and
    // "noDestination" above already blocks every such case.
    if (isMoveOperation && moveTo.destinationMoveCheck === "holdsQueuedItems") {
        return "alreadyAtDestination";
    }

    // Not yet verified — treated exactly like a destination that is still loading,
    // because that is what it means. Anything other than a positive "clear" blocks.
    if (isMoveOperation && moveTo.destinationMoveCheck !== "clear") {
        return "destinationLoading";
    }

    // Before the permission read, not after: while the destination is loading that
    // snapshot still describes the library the user just left.
    if (isContentLoading === true) {
        return "destinationLoading";
    }

    const permissionStatus = currentSharepointFolder?.permissionStatus;
    if (permissionStatus === "loading" || permissionStatus === "unknown" || !permissionStatus) {
        return "permissionPending";
    }
    if (permissionStatus !== "loaded" || currentSharepointFolder?.userPermissions?.canEditListItem !== true) {
        return "noPermission";
    }

    return null;
};

export const resolveCopyMoveTrayState = ({
    moveTo,
    currentDirectory,
    currentSharepointFolder,
    isContentLoading,
}: ICopyMoveTrayInputs): ICopyMoveTrayState => {
    if (moveTo?.moveToEnabled !== true) {
        return HIDDEN;
    }

    // Email copy modes keep their own long-standing banner; this tray covers the
    // document operations, which had none.
    if (!DOCUMENT_MODES.includes(moveTo.mode)) {
        return HIDDEN;
    }

    const isMoveOperation = moveTo.mode === ContentManagerCopyMoveModeEnum.Move
        || moveTo.mode === ContentManagerCopyMoveModeEnum.MoveAll;
    const isAllItems = moveTo.mode === ContentManagerCopyMoveModeEnum.MoveAll
        || moveTo.mode === ContentManagerCopyMoveModeEnum.CopyAll;

    const blocker = resolveBlocker(moveTo, isMoveOperation, currentDirectory, currentSharepointFolder, isContentLoading);

    return {
        visible: true,
        isMoveOperation,
        isAllItems,
        itemCount: moveTo.progressCountTotal > 0 ? moveTo.progressCountTotal : (moveTo.selectedFiles?.length ?? 0),
        sourceLabel: moveTo.sourceDisplayName ? moveTo.sourceDisplayName : (moveTo.sourceServerRelPath ?? ""),
        canCommit: blocker === null,
        blocker,
    };
};
